import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import { APIKey } from 'constants/apiKeys';
import {
  InstantReflex,
  InstantReflexVersionRef,
  ReflexFeedbackRating,
} from 'shiftNotes/_types';
import { useSubmitReflexFeedbackMutation } from 'shared/hooks/api/shiftNotes';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

import NoteCardExpandable from '../NotesStack/NoteCardExpandable';

import SectionBody from './SectionBody';
import VersionSelector from './VersionSelector';
import { buildSectionLabel, computeInitialFeedbackState } from './helpers';
import { Phase, StepFeedback } from './types';
import useReflexVersions from './useReflexVersions';

interface ReflexFeedbackSectionProps {
  instantReflex: InstantReflex;
  noteId: string;
  noteInstantReflexes?: InstantReflex[];
}

export default function ReflexFeedbackSection({
  instantReflex,
  noteId,
  noteInstantReflexes,
}: ReflexFeedbackSectionProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const capture = usePosthogCapture();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!noteInstantReflexes) return;
    noteInstantReflexes.forEach((reflex) => {
      queryClient.setQueryData<InstantReflex>(
        [APIKey.INSTANT_REFLEXES, reflex.id],
        (existing) => existing ?? reflex
      );
    });
  }, [noteInstantReflexes, queryClient]);

  const initialState = useMemo(
    () => computeInitialFeedbackState(instantReflex),
    [instantReflex]
  );
  const [currentStep, setCurrentStep] = useState(initialState.currentStep);
  const [feedback, setFeedback] = useState<Record<string, StepFeedback>>(
    initialState.feedback
  );
  const [overallComment, setOverallComment] = useState(
    initialState.overallComment
  );
  const [phase, setPhase] = useState<Phase>(initialState.phase);
  const [hasVisitedSubmitStep, setHasVisitedSubmitStep] = useState(false);

  const {
    latestVersion,
    versions,
    setSelectedVersionId,
    selectedVersionRef,
    isViewingOldVersion,
    activeReflex,
    isLoadingVersion,
    answerSteps,
    guidanceSteps,
    ratedSteps,
    isEmptyReflex,
  } = useReflexVersions({ instantReflex, noteInstantReflexes });

  const { mutate: submitFeedback, isPending } =
    useSubmitReflexFeedbackMutation();

  const hasSubmitStep = !isViewingOldVersion;
  const totalSteps = Math.max(ratedSteps.length + (hasSubmitStep ? 1 : 0), 1);
  const boundedStep = Math.min(currentStep, totalSteps);
  const isSubmitStep = hasSubmitStep && boundedStep === totalSteps;

  useEffect(() => {
    if (isSubmitStep) setHasVisitedSubmitStep(true);
  }, [isSubmitStep]);

  const activeIndex = boundedStep - 1;
  const activeStep = isSubmitStep ? null : ratedSteps[activeIndex];

  const allRated = ratedSteps.every(
    (step) => feedback[step.id]?.rating != null
  );
  const hasAnyFeedbackRating = ratedSteps.some(
    (step) => feedback[step.id]?.rating != null
  );
  const isLocked = phase !== 'editing' || isPending;
  const emptyReflexNeedsComment =
    isEmptyReflex && isSubmitStep && overallComment.trim() === '';
  const showApproveAllVariant =
    isSubmitStep &&
    !isEmptyReflex &&
    !hasAnyFeedbackRating &&
    !isViewingOldVersion &&
    phase === 'editing';

  const stepsCompletedStatus = [
    ...ratedSteps.map((step) =>
      isViewingOldVersion
        ? step.feedback_status != null
        : feedback[step.id]?.rating != null
    ),
    ...(hasSubmitStep ? [hasVisitedSubmitStep] : []),
  ];

  const label = buildSectionLabel({
    isLoadingVersion,
    isSubmitStep,
    isEmptyReflex,
    activeStep,
    answerSteps,
    guidanceSteps,
    t,
  });

  const variant =
    activeStep?.step_type === 'give_answer' ? 'answers' : 'guidance';

  const handlePrev = () => setCurrentStep((s) => Math.max(1, s - 1));

  const handleRatingChange = (stepId: string, rating: ReflexFeedbackRating) => {
    setFeedback((prev) => ({
      ...prev,
      [stepId]: { rating, comment: prev[stepId]?.comment ?? '' },
    }));
    capture.noteReflexFeedbackRated({
      shift_note_id: noteId,
      reflex_id: instantReflex.id,
      step_id: stepId,
      rating,
      version: latestVersion,
    });
    if (rating === 'positive' && !isLocked) {
      setCurrentStep((s) => Math.min(totalSteps, s + 1));
    }
  };

  const handleCommentChange = (stepId: string, comment: string) => {
    setFeedback((prev) => ({
      ...prev,
      [stepId]: { rating: prev[stepId]?.rating ?? null, comment },
    }));
  };

  const handleSubmit = () => {
    if (isLocked || emptyReflexNeedsComment) return;
    const bulkApprove = showApproveAllVariant;
    if (!bulkApprove && !allRated) return;

    const items = ratedSteps.map((step) =>
      bulkApprove
        ? {
            step_id: step.id,
            rating: 'positive' as ReflexFeedbackRating,
            comment: null,
          }
        : {
            step_id: step.id,
            rating: feedback[step.id].rating as ReflexFeedbackRating,
            comment: feedback[step.id].comment.trim() || null,
          }
    );
    const negativeCount = items.filter(
      (item) => item.rating === 'negative'
    ).length;
    const trimmedOverall = overallComment.trim();
    const willRegenerate = negativeCount > 0 || trimmedOverall !== '';

    if (willRegenerate) setPhase('regenerating');

    if (bulkApprove) {
      capture.noteReflexFeedbackBulkApproved({
        shift_note_id: noteId,
        reflex_id: instantReflex.id,
        version: latestVersion,
        step_count: ratedSteps.length,
      });
    }

    submitFeedback(
      {
        reflexId: instantReflex.id,
        noteId,
        section: 'reflex',
        overallComment: trimmedOverall || null,
        items,
      },
      {
        onSuccess: (data) => {
          capture.noteReflexFeedbackSubmitted({
            shift_note_id: noteId,
            reflex_id: instantReflex.id,
            version: latestVersion,
            section: 'reflex',
            negative_count: negativeCount,
            has_overall_comment: trimmedOverall !== '',
            regenerated: data.regenerated,
          });
          if (!data.regenerated) setPhase('thanks');
        },
        onError: () => {
          capture.noteReflexFeedbackSubmitError({
            shift_note_id: noteId,
            reflex_id: instantReflex.id,
            version: latestVersion,
            section: 'reflex',
          });
          setPhase('editing');
        },
      }
    );
  };

  const handleNext = () => {
    if (isSubmitStep) {
      handleSubmit();
      return;
    }
    setCurrentStep((s) => Math.min(totalSteps, s + 1));
  };

  const handleVersionSelect = (versionRef: InstantReflexVersionRef) => {
    setSelectedVersionId(
      versionRef.id === instantReflex.id ? null : versionRef.id
    );
    setCurrentStep(1);
    capture.noteReflexVersionSelected({
      shift_note_id: noteId,
      reflex_id: versionRef.id,
      version: versionRef.version,
    });
  };

  const showResult = phase !== 'editing' && isSubmitStep;
  const canGoPrev = isViewingOldVersion
    ? boundedStep > 1
    : boundedStep > 1 && phase === 'editing';
  const canGoNext = isViewingOldVersion
    ? boundedStep < totalSteps
    : phase === 'editing' &&
      (isSubmitStep
        ? (showApproveAllVariant || allRated) &&
          !isLocked &&
          !emptyReflexNeedsComment
        : true);

  return (
    <NoteCardExpandable
      variant={variant}
      collapsedLabel={label}
      expandedLabel={label}
      currentStep={boundedStep}
      totalSteps={totalSteps}
      collapsible={false}
      onPrev={handlePrev}
      onNext={handleNext}
      canGoPrev={canGoPrev}
      canGoNext={canGoNext}
      isLastStep={isSubmitStep}
      submitVariant={showApproveAllVariant ? 'approveAll' : 'submit'}
      showPagination={phase !== 'regenerating' && !isLoadingVersion}
      stepsCompletedStatus={stepsCompletedStatus}
      headerAccessory={
        <VersionSelector
          versions={versions}
          selectedVersionId={selectedVersionRef?.id ?? instantReflex.id}
          onVersionSelect={handleVersionSelect}
        />
      }
    >
      <SectionBody
        isLoadingVersion={isLoadingVersion}
        showResult={showResult}
        showThanks={phase === 'thanks'}
        isSubmitStep={isSubmitStep}
        isEmptyReflex={isEmptyReflex}
        isLocked={isLocked}
        isViewingOldVersion={isViewingOldVersion}
        overallComment={overallComment}
        onOverallCommentChange={setOverallComment}
        activeReflex={activeReflex}
        activeStep={activeStep}
        noteId={noteId}
        feedback={feedback}
        onRatingChange={handleRatingChange}
        onCommentChange={handleCommentChange}
      />
    </NoteCardExpandable>
  );
}
